// Library Imports
import axios from "axios";
import { signOut } from "firebase/auth";

// Local Imports
import { baseURL, endPoints } from "./constant";
import { logout, updateTokens } from "../reducers/authReducers/authReducers";
import { showToast } from "../../components/shared";
import { auth } from "../../lib/firebase";
import { useSecurityOtpStore } from "../../lib/securityOtpStore";
import { createRefreshQueue } from "./refreshQueue";

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
});

export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

// Synchronous token bootstrap — runs before any React component mounts
try {
  const persistedRoot = localStorage.getItem('persist:root');
  if (persistedRoot) {
    const parsed = JSON.parse(persistedRoot);
    const userState = JSON.parse(parsed?.user || '{}');
    const accessToken = userState?.user?.token?.access;
    if (accessToken) {
      setAuthToken(accessToken);
    }
  }
} catch (_) {
  /* ignore */
}

// Endpoints that should not trigger token refresh or session-expired logout
const AUTH_ENDPOINTS = ["/login/", "/registration/", "/forget-password/", "/reset-password/", "/token/refresh/", "/google-auth/"];

const isAuthEndpoint = (url) =>
  AUTH_ENDPOINTS.some((ep) => url?.includes(ep));

// OTP endpoints must never trigger the step-up flow themselves
const isOtpEndpoint = (url) => url?.includes("/security/otp/");

let isRefreshing = false;
const refreshQueue = createRefreshQueue();

const onRefreshed = (newAccessToken) => {
  refreshQueue.resolve(newAccessToken);
};

const onRefreshFailed = (error) => {
  refreshQueue.reject(error);
};

const forceLogout = async () => {
  const { store, persistor } = await import("../store");
  store.dispatch(logout());
  persistor.purge();
  setAuthToken(null);
  if (auth) signOut(auth);
  window.location.href = "/login?session_expired=1";
};

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!navigator.onLine) {
      console.log("No internet connection");
    }

    // Handle rate limit error
    if (
      error?.response?.status === 429 ||
      (error?.response?.data?.status === false &&
        error?.response?.data?.message ===
          "Rate limit exceeded. Please try again later.")
    ) {
      showToast("Rate limit exceeded. Please try again later.", "error", {
        position: "top-right",
        autoClose: 5000,
      });
    }

    const originalRequest = error.config;

    if (
      error?.response?.status === 401 &&
      !isAuthEndpoint(originalRequest?.url) &&
      !originalRequest._retry
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.add((newToken) => {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          }, reject);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { store } = await import("../store");
        const refreshToken = store.getState()?.user?.user?.token?.refresh;

        if (!refreshToken) {
          onRefreshFailed(error);
          await forceLogout();
          return Promise.reject(error);
        }

        const { data } = await axios.post(endPoints.TokenRefresh, {
          refresh: refreshToken,
        });

        const newAccess = data?.access;
        const newRefresh = data?.refresh || refreshToken;

        store.dispatch(updateTokens({ access: newAccess, refresh: newRefresh }));
        setAuthToken(newAccess);
        onRefreshed(newAccess);

        originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        onRefreshFailed(refreshError);
        await forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Global OTP step-up: sensitive endpoints answer 403 { code: "otp_required" }.
    // Request a challenge, open the global modal, and hold the original request
    // pending until the user verifies (retry once) or cancels (reject).
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.code === "otp_required" &&
      originalRequest &&
      !originalRequest._otpRetried &&
      !isOtpEndpoint(originalRequest?.url)
    ) {
      originalRequest._otpRetried = true;
      const otpStore = useSecurityOtpStore.getState();
      // One step-up at a time — a second caller gets the original error
      if (otpStore.isOpen) return Promise.reject(error);
      try {
        const { data } = await axiosInstance.post(endPoints.SecurityOtpRequest, {
          purpose: "SENSITIVE_ACTION",
        });
        const challengeId = data?.data?.challenge_id ?? data?.challenge_id;
        if (!challengeId) return Promise.reject(error);

        return new Promise((resolve, reject) => {
          useSecurityOtpStore.getState().open(challengeId, {
            resolve: () => resolve(axiosInstance(originalRequest)),
            reject: () => reject(error),
          });
        });
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
